import {
  DateDurationConfig,
  DateDurationField,
  DatePickerField,
  defaultDurationOptions,
  Flex
} from '@sequencehq/core-components'
import { Primary13SemiBold } from '@sequencehq/core-components'
import {
  phasesAtom,
  scheduleStartDateAtom
} from '../../atoms/reviewPricing.atoms'
import { useAtom, useAtomValue } from 'jotai'
import { dequal } from 'dequal'
import { useMemo } from 'react'
import { PhaseDuration } from '@sequencehq/utils'
import { usePhase } from '../../utils/usePhase'
import { useIsFirstPhase } from '../../utils/useIsFirstPhase'
import { HighlightFieldPathsOnHover } from '../../../../HighlightFieldPathsOnHover'

const getAvailablePhaseDurations = (params: {
  allowMilestoneBilling: boolean
  allowCustomPhaseDurations: boolean
  isLastPhase: boolean
  phaseDuration: PhaseDuration
}) => {
  return [
    ...defaultDurationOptions.filter(
      d => !['CUSTOM', 'FOREVER', 'CUSTOM_DURATION'].includes(d.value)
    ),
    {
      diff: {
        days: 0
      },
      label: 'Milestone based',
      value: 'MILESTONE'
    },
    defaultDurationOptions.find(d => d.value === 'FOREVER'),
    defaultDurationOptions.find(d => d.value === 'CUSTOM'),
    defaultDurationOptions.find(d => d.value === 'CUSTOM_DURATION')
  ]
    .filter((d): d is DateDurationConfig => Boolean(d))
    .filter(durationConfig => {
      /**
       * Remove milestone, forever, and custom if they are not available
       */
      if (durationConfig.value === 'FOREVER' && !params.isLastPhase) {
        return false
      }

      if (
        durationConfig.value === 'MILESTONE' &&
        !params.allowMilestoneBilling &&
        /**
         * Account for the scenario where milestone was already set and the flag
         * is now off. It's 'trap door' state - once the value is changed it
         * can't go back - but it keeps things looking correct.
         */
        !dequal(params.phaseDuration, { days: 0 })
      ) {
        return false
      }

      if (
        durationConfig.value === 'CUSTOM' &&
        !params.allowCustomPhaseDurations
      ) {
        return false
      }

      return true
    })
}

const usePhaseDurationField = (props: { phaseId: string }) => {
  const { phase, updatePhase } = usePhase(props.phaseId)
  const phaseStartDate = phase.startDate
  const phases = useAtomValue(phasesAtom)

  const phaseDurationOptions = useMemo(() => {
    if (!phase) return []

    const isLastPhase = phases.at(-1)?.id === phase.id

    return getAvailablePhaseDurations({
      allowMilestoneBilling: true,
      allowCustomPhaseDurations: true,
      isLastPhase,
      phaseDuration: phase.duration
    })
  }, [phase, phases])

  const phaseDurationValue = useMemo(() => {
    if (phase.duration === 'OPEN_ENDED') {
      return undefined
    }

    if (phase.duration === 'MILESTONE') {
      /**
       * We use this duration as the value to translate a milestone
       * to as this means the 'custom date' option will load with
       * today when selected, if already on the Milestone option.
       *
       * This need to translate to a duration for the value is a little
       * weird, and shows that the date duration input use case has extended
       * to values that don't strictly have a duration that can be modelled
       * correctly. Maybe we should allow for passing in a string value
       * to the input as an option alongside a date or duration?
       *
       * However, this is one to visit more in isolation. The extension to
       * the input to provide the matched config covers this usecase mostly
       * well enough, and extending the input further to cover this one
       * particular case may lead to an even more complicated field than
       * is necessary!
       */
      return {
        days: 0
      }
    }

    return phase.duration
  }, [phase.duration])

  return {
    phaseDurationOptions,
    phaseDurationValue,
    fromDate: phaseStartDate,
    updatePhaseDuration: (newDuration?: PhaseDuration) => {
      updatePhase({
        duration: newDuration ?? 'OPEN_ENDED'
      })
    }
  }
}

const usePhaseStartDateField = (props: { phaseId: string }) => {
  const [startDate, setStartDate] = useAtom(scheduleStartDateAtom)
  const { phase } = usePhase(props.phaseId)
  const phaseStartDate = phase.startDate
  const isFirstPhase = useIsFirstPhase(props.phaseId)

  return {
    startDate: isFirstPhase ? startDate : phaseStartDate,
    updateStartDate: (newDate: Date | undefined) => {
      if (newDate) {
        setStartDate(newDate)
      }
    },
    disabled: !isFirstPhase
  }
}

export const PhaseDateFields = (props: {
  phaseId: string
  phaseFieldPath: string
}) => {
  const {
    phaseDurationOptions,
    phaseDurationValue,
    fromDate,
    updatePhaseDuration
  } = usePhaseDurationField({
    phaseId: props.phaseId
  })

  const {
    startDate,
    updateStartDate,
    disabled: startDateDisabled
  } = usePhaseStartDateField({
    phaseId: props.phaseId
  })

  return (
    <Flex
      gap="16px"
      data-testid={`reviewPricing.phase.${props.phaseId}.dateFields`}
    >
      <HighlightFieldPathsOnHover
        fieldPaths={[`${props.phaseFieldPath}.startDate`]}
      >
        <DatePickerField
          data-testid={`reviewPricing.phase.${props.phaseId}.startDate`}
          variant="new"
          width="226px"
          label="Start date"
          placeholder="Not set"
          value={startDate}
          isDisabled={startDateDisabled}
          onChange={updateStartDate}
          styles={{
            label: {
              ...Primary13SemiBold
            }
          }}
        />
      </HighlightFieldPathsOnHover>
      <HighlightFieldPathsOnHover
        fieldPaths={[
          `${props.phaseFieldPath}.duration`,
          `${props.phaseFieldPath}.endDate`
        ]}
      >
        <DateDurationField
          data-testid={`reviewPricing.phase.${props.phaseId}.duration`}
          width="226px"
          label="Duration"
          durations={phaseDurationOptions}
          mode="relative"
          fromDate={fromDate}
          value={phaseDurationValue}
          onChange={updatePhaseDuration}
          styles={{
            label: {
              ...Primary13SemiBold
            }
          }}
          height="32px"
          inputStyleVariant="new"
        />
      </HighlightFieldPathsOnHover>
    </Flex>
  )
}
